<div class="device-selection">
  <!-- Header -->
  <div class="section-header">
    <mat-icon class="section-icon">devices</mat-icon>
    <div>
      <h2 class="section-title">Select Your Donation Collection Devices</h2>
      <p class="section-subtitle">
        <strong>✨ Unlock Advanced Fundraising Tools</strong> with smart pricing that grows with your organization.
      </p>
    </div>
  </div>

  <mat-divider></mat-divider>

  <!-- Existing Orders Summary -->
  <div *ngIf="existingOrders.length > 0" class="existing-orders" [class.completed]="hideNewOrderForm">
    <div class="orders-header">
      <mat-icon>check_circle</mat-icon>
      <h3>{{ hideNewOrderForm ? 'Device Orders - Complete ✓' : 'Your Device Orders' }}</h3>
    </div>
    <div *ngFor="let order of existingOrders" class="order-summary">
      <div class="order-id">Order: {{ order.OrderId }}</div>
      <div class="order-status">
        <span class="status-badge" [ngClass]="'status-' + order.Status">
          {{ order.Status | uppercase }}
        </span>
      </div>
      <div class="order-items">
        <div *ngFor="let item of order.Items" class="order-item">
          {{ item.quantity }}x {{ item.deviceType | titlecase }} - ${{ (item.quantity * item.unitPrice / 100).toFixed(2) }}
        </div>
      </div>
      <div class="order-total">
        Total Charged: <strong>${{ (order.TotalChargeCents / 100).toFixed(2) }}</strong>
      </div>
    </div>
    <div *ngIf="!hideNewOrderForm" class="button-group">
      <button mat-stroked-button (click)="orderMore()" class="order-more-btn">
        <mat-icon>add</mat-icon>
        <span>Order More Devices</span>
      </button>
    </div>
    <div *ngIf="hideNewOrderForm" class="button-group">
      <button mat-flat-button color="primary" (click)="continueOnboarding()" class="continue-btn">
        <mat-icon>arrow_forward</mat-icon>
        <span>Continue to Billing</span>
      </button>
    </div>
  </div>

  <div *ngIf="existingOrders.length > 0" class="section-divider">
    <mat-divider></mat-divider>
    <span class="divider-text">OR</span>
    <mat-divider></mat-divider>
  </div>

  <!-- Device options -->
  <div class="devices-container">
    <div *ngFor="let device of devices" class="device-card">
      <div class="device-header">
        <h3>{{ device.displayName }}</h3>
        <span *ngIf="device.type === 'kiosk'" class="badge badge-kiosk"
          >Stationary</span
        >
        <span *ngIf="device.type === 'collector'" class="badge badge-collector"
          >Mobile</span
        >
        <span *ngIf="device.type === 'on-the-go'" class="badge badge-on-go"
          >Portable</span
        >
      </div>

      <p class="device-description">{{ device.description }}</p>

      <div class="device-features">
        <div *ngFor="let feature of device.features" class="feature-item">
          <mat-icon>check_circle</mat-icon>
          <span>{{ feature }}</span>
        </div>
      </div>

      <!-- SIM Card Option -->
      <div *ngIf="device.simIncluded" class="sim-option">
        <mat-checkbox
          [(ngModel)]="device.simChecked"
          (change)="updateCart()"
          class="sim-checkbox">
          <strong>Include SIM Card</strong>
          <span class="sim-description">
            {{ device.type === 'on-the-go' ? 'Standalone connectivity for outdoor use' : 'Monthly connectivity: $10/month' }}
          </span>
        </mat-checkbox>
        <span *ngIf="device.simChecked" class="sim-note">
          ✓ First month ($10) included in today's charge. Then $10/month automatically billed.
          <br>
          <em *ngIf="device.type === 'on-the-go'">Uncheck if you have WiFi/hotspot available</em>
        </span>
      </div>

      <mat-divider></mat-divider>

      <div class="device-pricing">
        <div class="price-breakdown">
          <span class="price-label">First device:</span>
          <span class="price-value">$399</span>
        </div>
        <div class="price-breakdown">
          <span class="price-label">Additional:</span>
          <span class="price-value">$299 each</span>
        </div>
        <div class="price-breakdown">
          <span class="price-label">Setup fee:</span>
          <span class="price-value">$100 per visit</span>
        </div>
      </div>

      <div class="device-quantity">
        <button
          mat-icon-button
          (click)="removeDevice(device)"
          [disabled]="device.quantity === 0"
          class="qty-btn"
        >
          <mat-icon>remove</mat-icon>
        </button>
        <span class="qty-display">{{ device.quantity }}</span>
        <button
          mat-icon-button
          (click)="addDevice(device)"
          class="qty-btn"
        >
          <mat-icon>add</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <mat-divider></mat-divider>

  <!-- Payment Plan Selection -->
  <div *ngIf="hasDevices" class="payment-plan-section">
    <h3>How would you like to pay?</h3>
    <div class="plan-options">
      <label class="plan-option" [class.active]="cart.paymentPlan === 'now'">
        <input type="radio" name="paymentPlan" value="now" [checked]="cart.paymentPlan === 'now'" (change)="setPaymentPlan('now')" />
        <div class="plan-content">
          <strong>Pay Now</strong>
          <span class="plan-price">${{ formatPrice(cart.paymentPlan === 'now' ? cart.totalToday : 0) }} today</span>
          <span class="plan-desc">All devices + setup + tax + first month SIM <em>(higher due to taxes)</em></span>
        </div>
      </label>

      <label class="plan-option" [class.active]="cart.paymentPlan === '12months'">
        <input type="radio" name="paymentPlan" value="12months" [checked]="cart.paymentPlan === '12months'" (change)="setPaymentPlan('12months')" />
        <div class="plan-content">
          <strong>12-Month Plan</strong>
          <span class="plan-price">${{ formatPrice(cart.paymentPlan === '12months' ? cart.totalToday : 0) }} today, then ${{ formatPrice(cart.paymentPlan === '12months' ? cart.monthlyInstallment : 0) }}/month</span>
          <span class="plan-desc">Setup + SIM today, device payments spread over 12 months</span>
        </div>
      </label>
    </div>
  </div>

  <!-- Cart summary -->
  <div *ngIf="hasDevices" class="cart-summary">
    <h3>Order Summary</h3>
    <div class="cart-items">
      <div *ngFor="let item of cart.items" class="cart-item">
        <span class="item-description">
          {{ item.quantity }}x
          <strong>{{ item.deviceType | titlecase }}</strong>
          <span *ngIf="item.isFirstDevice" class="sale-badge">(Sale Price)</span>
        </span>
        <span class="item-price">${{ formatPrice(item.quantity * item.unitPrice) }}</span>
      </div>
    </div>

    <div class="cart-breakdown">
      <div class="breakdown-row">
        <span>Device(s) Subtotal:</span>
        <span>${{ formatPrice(cart.deviceSubtotal) }}</span>
      </div>
      <div *ngIf="cart.taxAmount > 0" class="breakdown-row">
        <span>Sales Tax ({{ (cart.taxRate * 100) | number:'1.0-0' }}%):</span>
        <span>${{ formatPrice(cart.taxAmount) }}</span>
      </div>
      <div class="breakdown-row">
        <span>Setup Fee (1 visit):</span>
        <span>${{ formatPrice(cart.setupFee) }}</span>
      </div>
      <div *ngIf="cart.simFees > 0" class="breakdown-row">
        <span>SIM Card(s) - First Month:</span>
        <span>${{ formatPrice(cart.simFees) }}</span>
      </div>

      <!-- Payment Plan Summary -->
      <div class="breakdown-row total" *ngIf="cart.paymentPlan === 'now'">
        <span><strong>Total Charge TODAY:</strong></span>
        <span><strong>${{ formatPrice(cart.totalToday) }}</strong></span>
      </div>

      <div *ngIf="cart.paymentPlan === '12months'" class="breakdown-12months">
        <div class="breakdown-row">
          <span><strong>Charge TODAY:</strong></span>
          <span><strong>${{ formatPrice(cart.totalToday) }}</strong></span>
          <span class="breakdown-note">(Setup + Tax + SIM first month)</span>
        </div>
        <div class="breakdown-row">
          <span><strong>Monthly for 12 months:</strong></span>
          <span><strong>${{ formatPrice(getMonthlyDevicePayment()) }}</strong> <em>(devices)</em> <span *ngIf="cart.simFees > 0">+ <strong>${{ formatPrice(cart.simFees) }}</strong> <em>(SIM)</em></span></span>
        </div>
        <div class="breakdown-row">
          <span><em>Total over 12 months:</em></span>
          <span><em>${{ formatPrice(cart.total) }}</em></span>
        </div>
        <div class="plan-note">
          ✓ Device payments spread evenly over 12 months (no interest)
          <br>
          ✓ SIM charge repeats monthly with your billing
        </div>
      </div>

      <div *ngIf="cart.simFees > 0" class="sim-note-breakdown">
        Then $10/month per SIM will be charged with your regular monthly billing
      </div>
    </div>

    <div class="warranty-note">
      ✓ 1-Year Warranty Included | All Sales Final
    </div>
  </div>

  <!-- Premium Features Info -->
  <div *ngIf="hasDevices" class="features-info">
    <h3>✨ What You'll Get</h3>
    <ul class="features-list">
      <li><strong>Unlimited donations</strong> — First 200/month free, then $0.10 per donation</li>
      <li><strong>Email campaigns</strong> — First 200/month free, then $0.005 per email</li>
      <li><strong>Smart scaling</strong> — Add devices anytime from your admin console</li>
      <li><strong>Real-time analytics</strong> — Track donations and campaigns live</li>
    </ul>
  </div>

  <!-- Actions -->
  <div class="actions">
    <button
      mat-flat-button
      color="primary"
      (click)="proceedToPayment()"
      [disabled]="!hasDevices"
      class="proceed-btn"
    >
      <mat-icon>shopping_cart</mat-icon>
      Proceed to Payment
    </button>

    <button
      mat-stroked-button
      (click)="skipDeviceSetup()"
      class="skip-btn"
    >
      I'll Add Devices Later
    </button>
  </div>
</div>
